import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  useColorModeValue,
  Icon,
  VStack,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/ApiContext';
import { CreateTeamWizard } from '@/components/team/CreateTeamWizard';

// Icons for team sections
const MyTeamsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    />
  </Icon>
);

const AllTeamsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-6c0-1.1.9-2 2-2s2 .9 2 2v6h8v-7.5c0-.83-.67-1.5-1.5-1.5S13 9.67 13 10.5V11H9V8.5C9 7.12 10.12 6 11.5 6S14 7.12 14 8.5V9h1v1.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V9c0-2.21-1.79-4-4-4-1.11 0-2.11.45-2.83 1.17C10.45 5.89 9.45 5.44 8.34 5.44 6.13 5.44 4.34 7.23 4.34 9.44V18z"
    />
  </Icon>
);

const RequestsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
    />
  </Icon>
);

const CreateTeamIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"
    />
  </Icon>
);

export const CreateTeamPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { TeamApi } = useApi();

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  // Modal state for wizard
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Open wizard on page load
  useEffect(() => {
    onOpen();
  }, [onOpen]);

  // Handle team creation
  const handleCreateTeam = async (teamData: any) => {
    try {
      setIsSubmitting(true);
      const response = await TeamApi.createTeam(teamData);
      const team = (response as any)?.data || response;

      toast({
        title: t('teams.createSuccess'),
        description: t('teams.teamCreatedSuccessfully'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Navigate to the new team
      if (team?.id) {
        navigate(`/teams/${team.id}`);
      } else {
        navigate('/teams/my-teams');
      }
    } catch (err: any) {
      console.error('Error creating team:', err);
      toast({
        title: t('teams.createError'),
        description: err.message || t('teams.failedToCreateTeam'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle wizard close (cancel)
  const handleWizardClose = () => {
    onClose();
    navigate('/teams/my-teams');
  };

  // Update sidebars with context actions and subsections
  useEffect(() => {
    // Update right sidebar with actions
    if (window.updateRightSidebar) {
      window.updateRightSidebar([
        {
          label: t('common.cancel'),
          colorScheme: 'gray',
          variant: 'outline',
          onClick: () => {
            handleWizardClose();
          },
        },
      ]);
    }

    // Update left sidebar with subsections info for teams
    if (window.updateLeftSidebar) {
      const teamSections = [
        {
          path: '/teams/my-teams',
          label: t('teams.myTeams'),
          icon: MyTeamsIcon,
        },
        {
          path: '/teams/all-teams',
          label: t('teams.allTeams'),
          icon: AllTeamsIcon,
        },
        {
          path: '/teams/requests',
          label: t('teams.requests'),
          icon: RequestsIcon,
        },
        {
          path: '/teams/create',
          label: t('teams.createTeam'),
          icon: CreateTeamIcon,
        },
      ];

      window.updateLeftSidebar({
        section: 'teams',
        subsections: teamSections
      });
    }

    return () => {
      if (window.updateRightSidebar) {
        window.updateRightSidebar([]);
      }
      // Don't clear left sidebar subsections - let them persist for navigation
    };
  }, [t]);

  return (
    <Box minH="100vh" bg={bgColor}>
      <Container maxW="7xl" py={6}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg" color="text.primary">
            {t('teams.createTeam')}
          </Heading>

          {/* CreateTeamWizard Modal */}
          <CreateTeamWizard
            isOpen={isOpen}
            onClose={handleWizardClose}
            onSubmit={handleCreateTeam}
            isSubmitting={isSubmitting}
          />

          {/* Fallback content when modal is closed */}
          {!isOpen && (
            <Box textAlign="center" py={12}>
              <CreateTeamIcon />
              <Heading size="md" mt={4} mb={2} color="text.primary">
                {t('teams.createTeam')}
              </Heading>
              <Box
                as="button"
                onClick={onOpen}
                bg="primary.500"
                color="white"
                px={6}
                py={3}
                borderRadius="md"
                _hover={{ bg: 'primary.600' }}
                cursor="pointer"
              >
                {t('teams.startCreating')}
              </Box>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
}; 